import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/metallic-v3.jpg";

export const Route = createFileRoute("/resources/garage-floor-coating-colors")({
  head: () => ({
    meta: [
      { title: "Garage Floor Coating Colors: Flake, Metallic & Solid | MLC Epoxy Floors" },
      { name: "description", content: "Explore garage floor color options — flake blends, metallic palettes, and solid colors. How to pick a color that fits your space and lifestyle." },
      { property: "og:title", content: "Garage Floor Coating Colors" },
      { property: "og:description", content: "Flake blends, metallic palettes, and solid colors with visual examples." },
      { property: "og:url", content: "/resources/garage-floor-coating-colors" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/resources/garage-floor-coating-colors" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify(buildBreadcrumbs([
        { name: "Home", href: "/" },
        { name: "Resources", href: "/resources" },
        { name: "Colors", href: "/resources/garage-floor-coating-colors" },
      ])),
    }],
  }),
  component: () => (
    <ResourceArticle
      title="Garage Floor Coating Colors"
      intro="Color is the most personal decision in any coating project. Here's how the three main systems — flake, metallic, and solid color — actually look on the floor."
      image={heroImg}
      sections={[
        {
          type: "prose",
          heading: "Three Systems, Very Different Looks",
          body: "Most homeowners assume the color choice is just picking a swatch. In reality, the system you choose dictates the texture, depth, and how the floor will read in different lighting. Flake gives you a textured, terrazzo-like finish that hides imperfections. Metallic creates a deep, three-dimensional swirl. Solid color is the clean industrial look.",
        },
        {
          type: "swatches",
          heading: "Popular Flake Blends",
          intro: "Vinyl chips broadcast into a pigmented epoxy base — the most common choice for residential garages. Hides minor concrete imperfections and provides slip resistance.",
          swatches: [
            { name: "Domino", color: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 40%, #d4d4d4 41%, #2d2d2d 60%, #1a1a1a 100%)", desc: "Black base with white and silver flake" },
            { name: "Sandstone", color: "linear-gradient(135deg, #8b7355 0%, #a89177 50%, #6b5841 100%)", desc: "Warm tan with brown and cream flake" },
            { name: "Copper Canyon", color: "linear-gradient(135deg, #3a2a1f 0%, #b87333 50%, #2a1810 100%)", desc: "Charcoal base with copper and bronze flake" },
            { name: "Storm Gray", color: "linear-gradient(135deg, #4a4a4a 0%, #6b6b6b 50%, #2d2d2d 100%)", desc: "Medium gray base with white, black, and silver" },
            { name: "Granite", color: "linear-gradient(135deg, #2d2d2d 0%, #5a5a5a 50%, #1a1a1a 100%)", desc: "Multi-tone gray that mimics natural stone" },
            { name: "Tuxedo", color: "linear-gradient(135deg, #0d0d0d 0%, #3a3a3a 50%, #0d0d0d 100%)", desc: "Pure black base with white and gray flake" },
          ],
        },
        {
          type: "swatches",
          heading: "Metallic Palette Highlights",
          intro: "Reflective metallic pigments suspended in clear epoxy, manipulated during install to create depth and movement. Each floor is one-of-a-kind.",
          swatches: [
            { name: "Liquid Copper", color: "linear-gradient(135deg, #5a2d1a 0%, #b87333 50%, #2d1810 100%)", desc: "Rich copper with bronze movement" },
            { name: "Black Pearl", color: "linear-gradient(135deg, #0d0d0d 0%, #2d2d3d 50%, #0a0a0a 100%)", desc: "Deep black with subtle pearl shimmer" },
            { name: "Silver Smoke", color: "linear-gradient(135deg, #6b6b6b 0%, #b0b0b0 50%, #3a3a3a 100%)", desc: "Brushed silver with charcoal undertones" },
            { name: "Aged Bronze", color: "linear-gradient(135deg, #3d2817 0%, #8b6f47 50%, #2a1810 100%)", desc: "Warm bronze with patina effect" },
            { name: "Midnight Blue", color: "linear-gradient(135deg, #0a0a2a 0%, #1e3a5f 50%, #050515 100%)", desc: "Deep navy with electric blue movement" },
            { name: "Titanium", color: "linear-gradient(135deg, #4a4a52 0%, #8a8a92 50%, #2a2a32 100%)", desc: "Cool industrial gray-silver" },
          ],
        },
        {
          type: "swatches",
          heading: "Solid Colors",
          intro: "Pure pigmented epoxy or polyaspartic — clean, modern, and easy to keep visually consistent across large commercial spaces.",
          swatches: [
            { name: "Charcoal", color: "#2d2d2d" },
            { name: "Battleship Gray", color: "#6b7280" },
            { name: "Light Gray", color: "#9ca3af" },
            { name: "Pure White", color: "#f5f5f0" },
            { name: "Safety Red", color: "#a83232" },
            { name: "Deep Beige", color: "#a89177" },
          ],
        },
        {
          type: "list",
          heading: "How to Pick the Right Color",
          items: [
            { label: "Match your lighting", desc: "Dark colors look dramatic but show every bit of dust under bright LED. Lighter blends keep the space feeling open." },
            { label: "Think about what you'll see most", desc: "Tools, vehicles, and equipment look best against neutral grays. Showpiece cars pop on metallics." },
            { label: "Consider resale", desc: "Neutral flake blends (Storm Gray, Granite, Sandstone) appeal to the widest buyer pool." },
            { label: "Sample in your space", desc: "We bring physical sample boards to every estimate so you can see colors under your actual garage lighting." },
          ],
        },
      ]}
    />
  ),
});
